import React, { Fragment, useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useHistory, Link } from "react-router-dom";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment-timezone";
import Tooltip from "@mui/material/Tooltip";
import ReactModal from "react-modal";
import AsyncSelect from "react-select/async";
import Select, { components } from "react-select";
import NumberFormat from "react-number-format";
import { Datepicker } from "@mobiscroll/react";
import axios from "axios";

import ai from "../../helpers/axios";
import getApiErrorMessage from "../../helpers/get-api-error-message";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Loader from "../loader";
import currencyFormatter from "../../helpers/currencyFormatter";
import { LoadingButton } from "../shared/loading-button";
import Search from "../search";
import TextInput from "../text-input";
import {
  formatProspectOption,
  formatTripOption,
  formatContractOption,
  formatGroupedOptionsByStage,
} from "./helpers";
import shortenName from "../../helpers/shorten-name";
import PaymentUnlinkLedgerModal from "./payment-unlink-ledger-modal";
import RevertFundsModal from "../trips/revert-funds-modal";
import RevertIncidentalFundsModal from "../trips/revert-incidental-funds-modal";
import Checkbox from "@mui/material/Checkbox";

const TRANSACTION_TYPE_OPTIONS = [
  { value: "Credit Balance", label: "Credit Balance" },
  { value: "Debit Balance", label: "Debit Balance" },
];

const CATEGORY_OPTIONS_BY_TYPE = {
  "Debit Balance": [
    { value: "Flight Charges", label: "Flight Charges" },
    { value: "Catering", label: "Catering" },
    { value: "Ground Transportation", label: "Ground Transportation" },
    { value: "Other Incidentals", label: "Other Incidentals" },
    { value: "Membership Canceled", label: "Membership Canceled" },
    { value: "Returned Funds on File", label: "Returned Funds on File" },
  ],
  "Credit Balance": [
    { value: "Account Activation", label: "Account Activation" },
    { value: "Refund - Partial Trip", label: "Refund - Partial Trip" },
    { value: "Refund - Canceled Trip", label: "Refund - Canceled Trip" },
    { value: "Refund - Overpayment", label: "Refund - Overpayment" },
    { value: "Funds on File (General)", label: "Funds on File (General)" },
    { value: "Membership Credits", label: "Membership Credits" },
  ],
};

const ALL_CATEGORY_OPTIONS = [
  ...CATEGORY_OPTIONS_BY_TYPE["Debit Balance"],
  ...CATEGORY_OPTIONS_BY_TYPE["Credit Balance"],
];

const SOURCE_OPTIONS = [
  { value: "Bank Transfer", label: "Bank Transfer" },
  { value: "Credit Card", label: "Credit Card" },
  { value: "Funds on File (General)", label: "Funds on File (General)" },
  { value: "Funds on File (Membership)", label: "Funds on File (Membership)" },
  { value: "Membership Credits", label: "Membership Credits" },
];

const TRIP_VISIBLE_CATEGORIES = [
  "Flight Charges",
  "Catering",
  "Ground Transportation",
  "Other Incidentals",
  "Refund - Partial Trip",
  "Refund - Canceled Trip",
  "Refund - Overpayment",
];

const INCIDENTAL_DEBIT_CATEGORIES = [
  "Catering",
  "Ground Transportation",
  "Other Incidentals",
];

const REFUND_CATEGORIES = [
  "Refund - Partial Trip",
  "Refund - Canceled Trip",
  "Refund - Overpayment",
];

const WITHDRAW_FROM_OPTIONS = [
  { value: "Contract", label: "Contract" },
  { value: "Incidentals", label: "Incidentals" },
];

const isIncidentalDebitCategory = (transactionType, category) =>
  transactionType === "Debit Balance" && INCIDENTAL_DEBIT_CATEGORIES.includes(category);

const isRefundCategory = (transactionType, category) =>
  transactionType === "Credit Balance" && REFUND_CATEGORIES.includes(category);

const targetsIncidentals = (transactionType, category, withdrawFrom) =>
  isIncidentalDebitCategory(transactionType, category) ||
  (isRefundCategory(transactionType, category) && withdrawFrom === "Incidentals");

const DEFAULT_OPTIONS_MAX_LIMIT = 50;

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return { ...state, isLoading: true };
    }

    case "dataDownloaded": {
      return {
        ...state,
        isLoading: false,
        data: action?.payload?.data?.data || [],
        totalLength: action?.payload?.data?.countTotal || 0,
      };
    }

    case "summaryDataDownloaded": {
      return {
        ...state,
        isSummaryLoading: false,
        summary: action?.payload?.data || {},
      };
    }

    case "filterOptionsLoaded": {
      return {
        ...state,
        clientOptions: action.payload.clients || [],
        ownerOptions: action.payload.owners || [],
        onBehalfOfOptions: action.payload.onBehalfOfs || [],
      };
    }

    case "pageChanged": {
      return { ...state, currentPage: action.payload.page };
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }

    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
        currentPage: 1,
      };
    }

    case "search": {
      return { ...state, query: action.query, currentPage: 1 };
    }

    case "closeModal": {
      return {
        ...state,
        selectedItem: null,
        isEditModalOpen: false,
        isDeleteModalOpen: false,
        revertModal: { open: false, tripId: null, transferGroupId: null, isIncidental: false, sourceInfo: null, targetInfo: null },
      };
    }

    case "fieldChanged": {
      return { ...state, [action.field]: action.value };
    }

    case "filterFieldChanged": {
      return { ...state, [action.field]: action.value, currentPage: 1 };
    }

    case "selectedItemFieldChanged": {
      return {
        ...state,
        selectedItem: {
          ...(state.selectedItem || {}),
          [action.field]: action.value,
        },
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  isLoading: true,
  isSaving: false,
  isSummaryLoading: true,
  summary: {},
  data: [],
  totalLength: 0,
  countPerPage: 50,
  currentPage: 1,
  sortBy: "createdAt",
  sortDirection: "desc",
  query: "",
  dateRange: null,
  clientFilters: [],
  ownerFilters: [],
  onBehalfOfFilters: [],
  categoryFilters: [],
  sourceFilters: [],
  clientOptions: [],
  ownerOptions: [],
  onBehalfOfOptions: [],
  isEditModalOpen: false,
  isDeleteModalOpen: false,
  isUnlinkModalOpen: false,
  selectedItem: null,
  itemToUnlink: null,
  revertModal: { open: false, tripId: null, transferGroupId: null, isIncidental: false, sourceInfo: null, targetInfo: null },
  refreshDataToken: Date.now(),
};

const getProspects = async (token, params = {}) => {
  const { data } = await ai.auth(token).get("/api/prospects/search", { params });
  return data?.prospects ?? [];
};

const getProspectOptions = (prospects) =>
  prospects.flatMap(({ _id, firstAndLastName, lastName, firstName, emails, companyName, assigneeId, assigneeName }) => {
    if (!firstAndLastName && !lastName && !firstName) return [];
    return [formatProspectOption({
      _id,
      firstAndLastName,
      lastName,
      firstName,
      emails,
      companyName,
      assigneeId,
      assigneeName,
    })];
  });

const getTrips = async (token, params = {}) => {
  const { data } = await ai.auth(token).get("/api/trips/search", { params });
  return { data: data?.data ?? [], totalLength: data?.countTotal ?? 0 };
};

const getTripOptions = (trips) => trips.map(formatTripOption);

const getContracts = async (token, params = {}) => {
  const { data } = await ai.auth(token).get("/api/trips/contracts/search", { params });
  return { data: data?.data ?? [], totalLength: data?.countTotal ?? 0 };
};

const tableColumns = [
  {
    name: "Client",
    selector: "clientName",
    sortable: true,
    minWidth: "100px",
    cell: (row) => (
      <Tooltip title={row.clientName || ""}>
        <div className="two-line-content">{row.clientName || "N/A"}</div>
      </Tooltip>
    ),
  },
  {
    name: "On Behalf Of",
    selector: "onBehalfOf",
    sortable: true,
    minWidth: "145px",
    maxWidth: "170px",
    cell: (row) => {
      return (
        <Tooltip title={row.onBehalfOf || ""}>
          <div className="two-line-content">
            {row.onBehalfOf || "-"}
          </div>
        </Tooltip>
      );
    },
  },
  {
    name: "Owner",
    selector: "ownerName",
    sortable: true,
    minWidth: "110px",
    maxWidth: "140px",
    cell: (row) => shortenName(row.ownerName) || "N/A",
  },
  {
    name: "Date",
    selector: "createdAt",
    sortable: true,
    width: "80px",
    cell: (row) => row.createdAt ? moment(row.createdAt).format("M/D/YY") : "N/A",
  },
  {
    name: "Debit Balance",
    selector: "debitBalance",
    sortable: false,
    width: "130px",
    cell: (row) => {
      if (row.transactionType !== "Debit Balance") {
        return "-";
      }

      return `-${currencyFormatter.format(Math.abs(row.amount || 0))}`;
    },
  },
  {
    name: "Credit Balance",
    selector: "creditBalance",
    sortable: false,
    width: "130px",
    cell: (row) => {
      if (row.transactionType !== "Credit Balance") {
        return "-";
      }

      return currencyFormatter.format(row.amount || 0);
    },
  },
  {
    name: "Category",
    selector: "category",
    sortable: true,
    minWidth: "150px",
    cell: (row) => (
      <Tooltip title={row.notes || ""} disableHoverListener={!row.notes}>
        <div>{row.category || "-"}</div>
      </Tooltip>
    ),
  },
  {
    name: "Source",
    selector: "source",
    sortable: true,
    minWidth: "150px",
    cell: (row) => row.source || "-",
  },
  {
    name: "Trip #",
    selector: "trip.tripTag",
    sortable: false,
    width: "100px",
    cell: (row) => {
      if (!row.tripId || !row.trip?.tripTag) {
        return "-";
      }

      return (
        <Link className="ellipsis-cell" to={`/trips/${row.tripId}`} target="_blank" rel="noopener noreferrer">
          <Tooltip title={row.trip?.tripTag} arrow>
            {row.trip?.tripTag}
          </Tooltip>
        </Link>
      );
    },
  },
  {
    name: "Contract #",
    selector: "contract.contractTag",
    sortable: false,
    width: "120px",
    cell: (row) => row.contract?.contractTag || "-",
  },
];

const CompactValueContainer = ({ children, getValue, hasValue, ...props }) => {
  const selected = getValue();
  const count = selected.length;

  if (!hasValue || count <= 1) {
    return (
      <components.ValueContainer {...props} getValue={getValue} hasValue={hasValue}>
        {children}
      </components.ValueContainer>
    );
  }

  const childArray = React.Children.toArray(children);
  const input = childArray[childArray.length - 1];
  const firstValue = childArray[0];

  return (
    <components.ValueContainer {...props} getValue={getValue} hasValue={hasValue}>
      {firstValue}
      <span className="select-extra-count">+{count - 1}</span>
      {input}
    </components.ValueContainer>
  );
};

const COMPACT_SELECT_COMPONENTS = { ValueContainer: CompactValueContainer };

const FundsLedgerContent = () => {
  const history = useHistory();
  const { user, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const isAccountingLedger = user && user["https://new.evojets.com/appMetadata"]?.isAccountingLedger;

  useEffect(() => {
    if (!isAccountingLedger) {
      toast.error("You do not have permission to view this page.");
      history.push("/");
    }
  }, [isAccountingLedger, history]);

  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    document.title = "Funds Ledger | evoJets HQ";
  }, []);

  const getToken = useCallback(async () => {
    return getAccessTokenSilently().catch(() => loginWithRedirect());
  }, [getAccessTokenSilently, loginWithRedirect]);

  const getData = useCallback(async (params) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    dispatch({ type: "dataBeganDownloading" });

    try {
      const token = await getToken();
      const { data } = await ai.auth(token).get("/api/funds-ledger", { params, signal });
      dispatch({ type: "dataDownloaded", payload: { data } });
    } catch (error) {
      if (axios.isCancel(error)) return;
      dispatch({ type: "fieldChanged", field: "isLoading", value: false });
    }
  }, [getToken]);

  const getFilterOptions = useCallback(async () => {
    try {
      const token = await getToken();
      const { data } = await ai.auth(token).get("/api/funds-ledger/filter-options");

      if (data) {
        dispatch({
          type: "filterOptionsLoaded",
          payload: {
            clients: (data.clients || []).map((c) => ({ value: c, label: c })),
            owners: (data.owners || []).map((o) => ({ value: o, label: o })),
            onBehalfOfs: (data.onBehalfOfs || []).map((o) => ({ value: o, label: o })),
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  }, [getToken]);

  useEffect(() => {
    getFilterOptions();
  }, [getFilterOptions, state.refreshDataToken]);

  const getSummaryData = useCallback(async (params) => {
    dispatch({ type: "fieldChanged", field: "isSummaryLoading", value: true });
    try {
      const token = await getToken();
      const { data } = await ai.auth(token).get("/api/funds-ledger/summary", { params });
      dispatch({ type: "summaryDataDownloaded", payload: { data } });
    } catch (error) {
      dispatch({ type: "fieldChanged", field: "isSummaryLoading", value: false });
    }
  }, [getToken]);

  const debouncedGetSummaryData = useDebouncedCallback((params) => {
    getSummaryData(params);
  }, 500);

  useEffect(() => {
    debouncedGetSummaryData({
      query: state.query,
      dateRange: state.dateRange,
      clientFilters: state.clientFilters,
      ownerFilters: state.ownerFilters,
      onBehalfOfFilters: state.onBehalfOfFilters,
      categoryFilters: state.categoryFilters,
      sourceFilters: state.sourceFilters,
    });
  }, [
    debouncedGetSummaryData,
    state.query,
    state.dateRange,
    state.clientFilters,
    state.ownerFilters,
    state.onBehalfOfFilters,
    state.categoryFilters,
    state.sourceFilters,
    state.refreshDataToken,
  ]);

  const debouncedGetData = useDebouncedCallback((params) => {
    getData(params);
  }, 300);

  useEffect(() => {
    debouncedGetData({
      page: state.currentPage,
      countPerPage: state.countPerPage,
      sortBy: state.sortBy,
      sortDirection: state.sortDirection,
      query: state.query,
      dateRange: state.dateRange,
      clientFilters: state.clientFilters,
      ownerFilters: state.ownerFilters,
      onBehalfOfFilters: state.onBehalfOfFilters,
      categoryFilters: state.categoryFilters,
      sourceFilters: state.sourceFilters,
    });
  }, [
    debouncedGetData,
    state.currentPage,
    state.countPerPage,
    state.sortBy,
    state.sortDirection,
    state.query,
    state.dateRange,
    state.clientFilters,
    state.ownerFilters,
    state.onBehalfOfFilters,
    state.categoryFilters,
    state.sourceFilters,
    state.refreshDataToken,
  ]);

  const handlePageChange = useCallback((page, totalRows) => {
    dispatch({ type: "pageChanged", payload: { page, totalRows } });
  }, []);

  const handleRowsPerPageChange = useCallback((currentRowsPerPage, currentPage) => {
    dispatch({ type: "rowsPerPageChanged", payload: { currentRowsPerPage, currentPage } });
  }, []);

  const handleSort = useCallback((column, _) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc" ? "desc" : "asc"
        : "desc";
    dispatch({ type: "tableSort", payload: { sortBy: column.selector, sortDirection: newDirection } });
  }, [state.sortBy, state.sortDirection]);

  const handleSearchInputChange = useCallback((event) => {
    dispatch({ type: "search", query: event.target.value.toLowerCase() });
  }, []);

  const handleAdd = useCallback((event) => {
    event.preventDefault();
    dispatch({ type: "fieldChanged", field: "selectedItem", value: {} });
    dispatch({ type: "fieldChanged", field: "isEditModalOpen", value: true });
  }, []);

  const handleEdit = useCallback((event, item) => {
    event.preventDefault();
    const enrichedItem = {
      ...item,
      clientOption: item.clientId ? { value: item.clientId, label: item.clientName } : null,
      transactionTypeOption: TRANSACTION_TYPE_OPTIONS.find((o) => o.value === item.transactionType) || null,
      categoryOption: ALL_CATEGORY_OPTIONS.find((o) => o.value === item.category) || null,
      sourceOption: SOURCE_OPTIONS.find((o) => o.value === item.source) || null,
      tripOption: item.tripId && item.trip ? formatTripOption(item.trip) : null,
      contractOption: item.contractId && item.contract ? formatContractOption(item.contract) : null,
      notes: item.notes || "",
      // Rows saved before withdrawFrom was stored: recover it from what the row
      // is attached to, so opening a refund for a small edit doesn't force a
      // re-pick (which blanks the trip and contract).
      withdrawFrom:
        item.withdrawFrom ||
        (isRefundCategory(item.transactionType, item.category)
          ? (item.incidentalChargeTripId ? "Incidentals" : item.contractId ? "Contract" : null)
          : null),
    };
    dispatch({ type: "fieldChanged", field: "selectedItem", value: enrichedItem });
    dispatch({ type: "fieldChanged", field: "isEditModalOpen", value: true });
  }, []);

  const handleDelete = useCallback(
    async (event, item) => {
      event.preventDefault();

      if (item.transferGroupId) {
        dispatch({ type: "fieldChanged", field: "selectedItem", value: item });

        const isIncidental = !!item.incidentalChargeTripId;
        const endpointBase = isIncidental ? "incidental-funds-movement" : "funds-movement";

        const token = await getToken();
        const { data } = await ai
          .auth(token)
          .get(`/api/trips/${item.tripId}/${endpointBase}/${item.transferGroupId}/info`)
          .catch((error) => {
            toast.error(getApiErrorMessage(error, "Error loading funds movement."));
            return {};
          });

        dispatch({
          type: "fieldChanged",
          field: "revertModal",
          value: {
            open: true,
            tripId: item.tripId,
            transferGroupId: item.transferGroupId,
            isIncidental,
            sourceInfo: data?.source || null,
            targetInfo: data?.target || null,
          },
        });
        return;
      }

      dispatch({ type: "fieldChanged", field: "selectedItem", value: item });
      dispatch({ type: "fieldChanged", field: "isDeleteModalOpen", value: true });
    },
    [getToken],
  );

  const handleRevertConfirm = useCallback(async () => {
    const { transferGroupId, tripId, isIncidental } = state.revertModal;
    if (!transferGroupId || !tripId) return;

    dispatch({ type: "fieldChanged", field: "isSaving", value: true });

    const token = await getToken();
    const endpointBase = isIncidental ? "incidental-funds-movement" : "funds-movement";

    try {
      await ai.auth(token).delete(`/api/trips/${tripId}/${endpointBase}/${transferGroupId}/revert`);
      dispatch({ type: "fieldChanged", field: "refreshDataToken", value: Date.now() });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error reverting funds movement."));
    }

    dispatch({ type: "fieldChanged", field: "isSaving", value: false });
    dispatch({ type: "closeModal" });
  }, [getToken, state.revertModal]);

  const handleCloseModal = useCallback(() => {
    dispatch({ type: "closeModal" });
  }, []);

  const handleInputChange = useCallback((event) => {
    dispatch({
      type: "selectedItemFieldChanged",
      field: event.currentTarget.name,
      value: event.currentTarget.value,
    });
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    const item = state.selectedItem;
    const showTripSelect = TRIP_VISIBLE_CATEGORIES.includes(item?.category);
    const itemIsRefundCategory = isRefundCategory(item?.transactionType, item?.category);
    const itemTargetsIncidentals = targetsIncidentals(item?.transactionType, item?.category, item?.withdrawFrom);

    if (!item?.clientId || !item?.transactionType || (item?.amount == null || item?.amount === "") || !item?.category || !item?.source) {
      return toast.error("Please fill in all required fields and retry.");
    }

    if (item.transactionType === "Debit Balance" && item.category === "Other Incidentals" && !item.notes?.trim()) {
      return toast.error("Notes is required for Other Incidentals.");
    }

    if (itemIsRefundCategory && !item.withdrawFrom) {
      return toast.error("Please fill in all required fields and retry.");
    }

    if (showTripSelect && (!item.tripId || (!itemTargetsIncidentals && !item.contractId))) {
      return toast.error("Please fill in all required fields and retry.");
    }

    dispatch({ type: "fieldChanged", field: "isSaving", value: true });

    const token = await getToken();

    const data = {
      clientId: item.clientId,
      onBehalfOf: item.onBehalfOf || "",
      transactionType: item.transactionType,
      amount: parseFloat(item.amount),
      category: item.category,
      source: item.source,
      notes: item.notes || "",
      tripId: item.tripId || null,
      contractId: itemTargetsIncidentals ? null : (item.contractId || null),
      withdrawFrom: item.withdrawFrom || null,
      skipPaymentRecordAutoCreation: item.skipPaymentRecordAutoCreation || false,
    };

    let response;
    if (item._id) {
      response = await ai.auth(token).put(`/api/funds-ledger/${item._id}`, data).catch((error) => {
        toast.error(getApiErrorMessage(error, "Error saving record."));
      });
    } else {
      response = await ai.auth(token).post("/api/funds-ledger", data).catch((error) => {
        toast.error(getApiErrorMessage(error, "Error saving record."));
      });
    }

    dispatch({ type: "fieldChanged", field: "isSaving", value: false });

    if (response?.data) {
      dispatch({ type: "fieldChanged", field: "refreshDataToken", value: Date.now() });
      dispatch({ type: "closeModal" });
    }
  }, [state.selectedItem, getToken]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!state.selectedItem) return;

    dispatch({ type: "fieldChanged", field: "isSaving", value: true });

    const token = await getToken();
    const item = state.selectedItem;

    try {
      if (item.incidentalChargeTripId && item.incidentalChargeId) {
        await ai.auth(token).delete(`/api/trips/${item.incidentalChargeTripId}/unlink-incidental-ledger-transfer/${item.incidentalChargeId}`);
      } else if (item.clientPaymentTripId && item.clientPaymentId) {
        await ai.auth(token).delete(`/api/trips/${item.clientPaymentTripId}/unlink-ledger-transfer/${item.clientPaymentId}`);
      } else {
        await ai.auth(token).delete(`/api/funds-ledger/${item._id}`);
      }
      dispatch({ type: "fieldChanged", field: "refreshDataToken", value: Date.now() });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error deleting record."));
    }

    dispatch({ type: "fieldChanged", field: "isSaving", value: false });
    dispatch({ type: "closeModal" });
  }, [getToken, state.selectedItem]);

  const handleUnlinkFromTransaction = useCallback((row) => {
    if (!row.xeroTransactionId) return;
    dispatch({ type: "fieldChanged", field: "itemToUnlink", value: { _id: row.xeroTransactionId } });
    dispatch({ type: "fieldChanged", field: "isUnlinkModalOpen", value: true });
  }, []);

  const handleCloseUnlinkModal = useCallback((refresh = true) => {
    dispatch({ type: "fieldChanged", field: "isUnlinkModalOpen", value: false });
    dispatch({ type: "fieldChanged", field: "itemToUnlink", value: null });
    if (refresh) {
      dispatch({ type: "fieldChanged", field: "refreshDataToken", value: Date.now() });
    }
  }, []);

  const loadProspects = useCallback(async (inputValue) => {
    const token = await getToken();
    const prospects = await getProspects(token, { q: inputValue, limit: DEFAULT_OPTIONS_MAX_LIMIT });
    return getProspectOptions(prospects);
  }, [getToken]);

  const debouncedLoadProspects = useDebouncedCallback(
    (inputValue, callback) => {
      loadProspects(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const loadTrips = useCallback(async (inputValue) => {
    const token = await getToken();
    const filters = { q: inputValue, limit: DEFAULT_OPTIONS_MAX_LIMIT };
    if (state.selectedItem?.clientId) {
      filters.prospectFilter = state.selectedItem.clientId;
    }
    const { data: trips } = await getTrips(token, filters);
    return getTripOptions(trips);
  }, [getToken, state.selectedItem?.clientId]);

  const debouncedLoadTrips = useDebouncedCallback(
    (inputValue, callback) => {
      loadTrips(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const loadContracts = useCallback(async (inputValue) => {
    const token = await getToken();
    const filters = { q: inputValue, limit: DEFAULT_OPTIONS_MAX_LIMIT };
    if (state.selectedItem?.clientId) {
      filters.prospectFilter = state.selectedItem.clientId;
    }
    if (state.selectedItem?.tripId) {
      filters.tripFilter = state.selectedItem.tripId;
    }
    const { data: contracts } = await getContracts(token, filters);
    return formatGroupedOptionsByStage(contracts, formatContractOption, !state.selectedItem?.tripId);
  }, [getToken, state.selectedItem?.clientId, state.selectedItem?.tripId]);

  const debouncedLoadContracts = useDebouncedCallback(
    (inputValue, callback) => {
      loadContracts(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const showTripSelect = useMemo(
    () => TRIP_VISIBLE_CATEGORIES.includes(state.selectedItem?.category),
    [state.selectedItem?.category],
  );

  const showWithdrawFromSelect = useMemo(
    () => isRefundCategory(state.selectedItem?.transactionType, state.selectedItem?.category),
    [state.selectedItem?.transactionType, state.selectedItem?.category],
  );

  const selectedItemTargetsIncidentals = useMemo(
    () => targetsIncidentals(state.selectedItem?.transactionType, state.selectedItem?.category, state.selectedItem?.withdrawFrom),
    [state.selectedItem?.transactionType, state.selectedItem?.category, state.selectedItem?.withdrawFrom],
  );

  const showTripDetails = showTripSelect && (!showWithdrawFromSelect || !!state.selectedItem?.withdrawFrom);

  const categoryOptions = useMemo(
    () => (state.selectedItem?.transactionType ? CATEGORY_OPTIONS_BY_TYPE[state.selectedItem.transactionType] || [] : []),
    [state.selectedItem?.transactionType],
  );

  const columnsConfig = useMemo(() => ([
    ...tableColumns,
    {
      name: "Actions",
      selector: "actions",
      sortable: false,
      width: "90px",
      cell: (row) => (
        <div className="d-flex justify-content-end">
          {row.xeroTransactionId ? (
            <button
              className="base-button icon-button icon-button-small btn-danger"
              title="Unlink from Transaction"
              onClick={() => handleUnlinkFromTransaction(row)}
            >
              <span className="material-icons">link_off</span>
            </button>
          ) : row.transferGroupId ? (
            <button
              className="base-button icon-button icon-button-small btn-danger"
              title="Revert Funds Movement"
              onClick={(event) => handleDelete(event, row)}
            >
              <span className="material-icons">undo</span>
            </button>
          ) : row.clientPaymentId || row.incidentalChargeId ? (
            <button
              className="base-button icon-button icon-button-small btn-danger"
              title="Unlink from Ledger"
              onClick={(event) => handleDelete(event, row)}
            >
              <span className="material-icons">link_off</span>
            </button>
          ) : (
            <>
              <button
                className="base-button icon-button icon-button-small evo-blue"
                title="Edit Record"
                onClick={(event) => handleEdit(event, row)}
              >
                <span className="material-icons">edit</span>
              </button>
              <button
                className="base-button icon-button icon-button-small btn-danger ml-2"
                title="Delete Record"
                onClick={(event) => handleDelete(event, row)}
              >
                <span className="material-icons">delete</span>
              </button>
            </>
          )}
        </div>
      ),
    },
  ]), [handleEdit, handleDelete, handleUnlinkFromTransaction]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1={
          <h1 className="page-title d-flex">
            Funds Ledger
            <button
              className="base-button icon-button icon-button-small evo-dark-green ml-2"
              title="Add Ledger Record"
              onClick={handleAdd}
              disabled={state.isSaving}
            >
              <span className="material-icons">add_circle</span>
            </button>
          </h1>
        }
        pretitle="Business"
        breadcrumbs={[{ link: "/funds-ledger", label: "Funds Ledger" }]}
        extraClasses="page-header-trips-l1"
        rightColumn={
          <div id="trip-actions-row" className="position-relative">
            {state.isSummaryLoading && (
              <div className="position-absolute w-100 h-100 custom-loader-wrapper">
                <Loader additionalClasses="position-absolute top-50 start-50 translate-middle"/>
              </div>
            )}
            <div className="d-flex gap-20">
              <div className="funds-ledger-summary-group">
                <div className="funds-ledger-summary-row">
                  <span>Debit:</span>
                  <span>{state.summary?.cash?.debit ? currencyFormatter.format(state.summary.cash.debit) : "N/A"}</span>
                </div>
                <div className="funds-ledger-summary-row">
                  <span>Credit:</span>
                  <span>{state.summary?.cash?.credit ? currencyFormatter.format(state.summary.cash.credit) : "N/A"}</span>
                </div>
                <div className="funds-ledger-summary-row">
                  <span>NET CASH:</span>
                  <span>{state.summary?.cash?.net ? currencyFormatter.format(state.summary.cash.net) : "N/A"}</span>
                </div>
              </div>

              <div className="funds-ledger-summary-group">
                <div className="funds-ledger-summary-row">
                  <span>Debit:</span>
                  <span>{state.summary?.credits?.debit ? currencyFormatter.format(state.summary.credits.debit) : "N/A"}</span>
                </div>
                <div className="funds-ledger-summary-row">
                  <span>Credit:</span>
                  <span>{state.summary?.credits?.credit ? currencyFormatter.format(state.summary.credits.credit) : "N/A"}</span>
                </div>
                <div className="funds-ledger-summary-row">
                  <span>NET CREDITS:</span>
                  <span>{state.summary?.credits?.net ? currencyFormatter.format(state.summary.credits.net) : "N/A"}</span>
                </div>
              </div>

              <div className="funds-ledger-summary-group">
                <div className="funds-ledger-summary-row">
                  <span>Debit:</span>
                  <span>{state.summary?.total?.debit ? currencyFormatter.format(state.summary.total.debit) : "N/A"}</span>
                </div>
                <div className="funds-ledger-summary-row">
                  <span>Credit:</span>
                  <span>{state.summary?.total?.credit ? currencyFormatter.format(state.summary.total.credit) : "N/A"}</span>
                </div>
                <div className="funds-ledger-summary-row">
                  <span>NET TOTAL:</span>
                  <span>{state.summary?.total?.net ? currencyFormatter.format(state.summary.total.net) : "N/A"}</span>
                </div>
              </div>
            </div>
          </div>
        }
      />

      <div className="funds-ledger-filters-bar">
        <div className="funds-ledger-filters-left">
          <div className="funds-ledger-filter-row">
            <Datepicker
              select="range"
              returnFormat="iso8601"
              controls={["date"]}
              value={state.dateRange}
              placeholder="__/__/____ - __/__/____"
              onChange={(range) => {
                dispatch({ type: "filterFieldChanged", field: "dateRange", value: range.value });
              }}
              themeVariant="light"
              inputProps={{ readOnly: true, className: "dates-range-light-input" }}
            />

            <Select
              isMulti
              classNamePrefix="select"
              className="accounting-filter"
              options={state.clientOptions}
              placeholder="Filter by Client"
              components={COMPACT_SELECT_COMPONENTS}
              onChange={(selectedValue) => {
                dispatch({
                  type: "filterFieldChanged",
                  field: "clientFilters",
                  value: selectedValue?.map(({ value }) => value) || [],
                });
              }}
              value={state.clientOptions.filter(({ value }) => state.clientFilters.includes(value))}
            />

            <Select
              isMulti
              classNamePrefix="select"
              className="accounting-filter"
              options={state.ownerOptions}
              placeholder="Filter by Owner"
              components={COMPACT_SELECT_COMPONENTS}
              onChange={(selectedValue) => {
                dispatch({
                  type: "filterFieldChanged",
                  field: "ownerFilters",
                  value: selectedValue?.map(({ value }) => value) || [],
                });
              }}
              value={state.ownerOptions.filter(({ value }) => state.ownerFilters.includes(value))}
            />
          </div>

          <div className="funds-ledger-filter-row">
            <Select
              isMulti
              classNamePrefix="select"
              className="accounting-filter"
              options={state.onBehalfOfOptions}
              placeholder="Filter by On Behalf Of"
              components={COMPACT_SELECT_COMPONENTS}
              onChange={(selectedValue) => {
                dispatch({
                  type: "filterFieldChanged",
                  field: "onBehalfOfFilters",
                  value: selectedValue?.map(({ value }) => value) || [],
                });
              }}
              value={state.onBehalfOfOptions.filter(({ value }) => state.onBehalfOfFilters.includes(value))}
            />

            <Select
              isMulti
              classNamePrefix="select"
              className="accounting-filter"
              options={ALL_CATEGORY_OPTIONS}
              placeholder="Filter by Category"
              components={COMPACT_SELECT_COMPONENTS}
              onChange={(selectedValue) => {
                dispatch({
                  type: "filterFieldChanged",
                  field: "categoryFilters",
                  value: selectedValue?.map(({ value }) => value) || [],
                });
              }}
              value={ALL_CATEGORY_OPTIONS.filter(({ value }) => state.categoryFilters.includes(value))}
            />

            <Select
              isMulti
              classNamePrefix="select"
              className="accounting-filter"
              options={SOURCE_OPTIONS}
              placeholder="Filter by Source"
              components={COMPACT_SELECT_COMPONENTS}
              onChange={(selectedValue) => {
                dispatch({
                  type: "filterFieldChanged",
                  field: "sourceFilters",
                  value: selectedValue?.map(({ value }) => value) || [],
                });
              }}
              value={SOURCE_OPTIONS.filter(({ value }) => state.sourceFilters.includes(value))}
            />
          </div>
        </div>

        <Search
          onSearch={handleSearchInputChange}
          placeholder="Search"
          query={state.query}
          className="search-input-stretch"
        />
      </div>

      <div className="server-pagination-table-container trips-list-table-container">
        <StickyHeaderDataTable
          columns={columnsConfig}
          data={state.data}
          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[5, 10, 25, 50, 100, 150, 200, 500]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.isLoading}
          progressComponent={<Loader/>}
          persistTableHead
          highlightOnHover
          responsive
        />
      </div>

      <ReactModal
        isOpen={state.isEditModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: { zIndex: 999999, backgroundColor: "rgb(32 53 68 / 50%)" },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "700px",
            maxHeight: "80vh",
            minWidth: "calc(min(700px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header">
          <h4>{state.selectedItem?._id ? "Edit" : "Add"} Ledger Record</h4>
        </div>

        <div className="modal-body width-100 modal-body-with-scroll">
          <form className="w-100 base-form mt-10">
            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-grow-1">
                <label className="base-label">
                  Client <span className="required">*</span>
                </label>
                <AsyncSelect
                  classNamePrefix="select"
                  loadOptions={debouncedLoadProspects}
                  placeholder="Search Client"
                  onChange={(option) => {
                    dispatch({ type: "selectedItemFieldChanged", field: "clientId", value: option?.value || null });
                    dispatch({ type: "selectedItemFieldChanged", field: "clientOption", value: option });
                    dispatch({ type: "selectedItemFieldChanged", field: "tripId", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "tripOption", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "contractId", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "contractOption", value: null });
                  }}
                  value={state.selectedItem?.clientOption || null}
                  noOptionsMessage={() => "Type to search..."}
                  isClearable
                  menuPosition="fixed"
                />
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <TextInput
                containerClasses="flex-grow-1"
                name="onBehalfOf"
                label="On Behalf Of"
                placeholder="Enter On Behalf Of"
                handleChange={handleInputChange}
                value={state.selectedItem?.onBehalfOf || ""}
              />
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label className="base-label">
                  Transaction Type <span className="required">*</span>
                </label>
                <Select
                  classNamePrefix="select"
                  options={TRANSACTION_TYPE_OPTIONS}
                  placeholder="Select Type"
                  onChange={(option) => {
                    dispatch({
                      type: "selectedItemFieldChanged",
                      field: "transactionType",
                      value: option?.value || null,
                    });
                    dispatch({ type: "selectedItemFieldChanged", field: "transactionTypeOption", value: option });
                    dispatch({ type: "selectedItemFieldChanged", field: "category", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "categoryOption", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "tripId", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "tripOption", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "contractId", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "contractOption", value: null });
                  }}
                  value={state.selectedItem?.transactionTypeOption || null}
                  menuPosition="fixed"
                />
              </div>

              <div className="form-group flex-half">
                <label className="base-label">
                  Amount <span className="required">*</span>
                </label>
                <NumberFormat
                  className="base-input"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  placeholder="$0.00"
                  value={state.selectedItem?.amount ?? ""}
                  onValueChange={({ floatValue }) => {
                    dispatch({ type: "selectedItemFieldChanged", field: "amount", value: floatValue });
                  }}
                />
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label className="base-label">
                  Category <span className="required">*</span>
                </label>
                <Select
                  key={state.selectedItem?.transactionType}
                  classNamePrefix="select"
                  options={categoryOptions}
                  placeholder="Select Category"
                  isDisabled={!state.selectedItem?.transactionType}
                  onChange={(option) => {
                    dispatch({ type: "selectedItemFieldChanged", field: "category", value: option?.value || null });
                    dispatch({ type: "selectedItemFieldChanged", field: "categoryOption", value: option });
                    dispatch({ type: "selectedItemFieldChanged", field: "withdrawFrom", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "tripId", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "tripOption", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "contractId", value: null });
                    dispatch({ type: "selectedItemFieldChanged", field: "contractOption", value: null });
                  }}
                  value={state.selectedItem?.categoryOption || null}
                  menuPosition="fixed"
                />
              </div>

              <div className="form-group flex-half">
                <label className="base-label">
                  Source <span className="required">*</span>
                </label>
                <Select
                  classNamePrefix="select"
                  options={SOURCE_OPTIONS}
                  placeholder="Select Source"
                  onChange={(option) => {
                    dispatch({ type: "selectedItemFieldChanged", field: "source", value: option?.value || null });
                    dispatch({ type: "selectedItemFieldChanged", field: "sourceOption", value: option });
                  }}
                  value={state.selectedItem?.sourceOption || null}
                  menuPosition="fixed"
                />
              </div>
            </div>

            {showWithdrawFromSelect && (
              <div className="w-100 d-flex gap-20">
                <div className="form-group flex-grow-1">
                  <label className="base-label">
                    Withdraw From <span className="required">*</span>
                  </label>
                  <Select
                    classNamePrefix="select"
                    options={WITHDRAW_FROM_OPTIONS}
                    placeholder="Select..."
                    onChange={(option) => {
                      // Re-selecting the current value must not wipe the trip/contract.
                      if ((option?.value || null) === (state.selectedItem?.withdrawFrom || null)) return;
                      dispatch({ type: "selectedItemFieldChanged", field: "withdrawFrom", value: option?.value || null });
                      dispatch({ type: "selectedItemFieldChanged", field: "tripId", value: null });
                      dispatch({ type: "selectedItemFieldChanged", field: "tripOption", value: null });
                      dispatch({ type: "selectedItemFieldChanged", field: "contractId", value: null });
                      dispatch({ type: "selectedItemFieldChanged", field: "contractOption", value: null });
                    }}
                    value={WITHDRAW_FROM_OPTIONS.find((o) => o.value === state.selectedItem?.withdrawFrom) || null}
                    menuPosition="fixed"
                  />
                </div>
              </div>
            )}

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-grow-1">
                <label className="base-label">
                  Notes{state.selectedItem?.transactionType === "Debit Balance" && state.selectedItem?.category === "Other Incidentals" &&
                  <span className="required"> *</span>}
                </label>
                <textarea
                  className="base-input"
                  name="notes"
                  placeholder="Enter notes"
                  rows={3}
                  value={state.selectedItem?.notes || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {showTripDetails && (
              <>
                <div className="w-100 d-flex gap-20">
                  <div className="form-group flex-grow-1">
                    <label className="base-label">
                      Trip # <span className="required">*</span>
                    </label>
                    <AsyncSelect
                      key={state.selectedItem?.clientId}
                      classNamePrefix="select"
                      loadOptions={debouncedLoadTrips}
                      placeholder="Search Trip"
                      onChange={(option) => {
                        dispatch({ type: "selectedItemFieldChanged", field: "tripId", value: option?.value || null });
                        dispatch({ type: "selectedItemFieldChanged", field: "tripOption", value: option });
                        dispatch({ type: "selectedItemFieldChanged", field: "contractId", value: null });
                        dispatch({ type: "selectedItemFieldChanged", field: "contractOption", value: null });
                        if (option?.clientId) {
                          dispatch({ type: "selectedItemFieldChanged", field: "clientId", value: option.clientId });
                          dispatch({
                            type: "selectedItemFieldChanged",
                            field: "clientOption",
                            value: { value: option.clientId, label: option.clientName || "" },
                          });
                        }
                      }}
                      value={state.selectedItem?.tripOption || null}
                      noOptionsMessage={() => "Type to search..."}
                      isClearable
                      menuPosition="fixed"
                    />
                  </div>
                </div>

                {!selectedItemTargetsIncidentals && (
                  <div className="w-100 d-flex gap-20">
                    <div className="form-group flex-grow-1">
                      <label className="base-label">
                        Contract # <span className="required">*</span>
                      </label>
                      <AsyncSelect
                        key={`${state.selectedItem?.clientId}-${state.selectedItem?.tripId}`}
                        classNamePrefix="select"
                        loadOptions={debouncedLoadContracts}
                        defaultOptions={!!(state.selectedItem?.clientId || state.selectedItem?.tripId)}
                        placeholder="Search Contract"
                        onChange={(option) => {
                          dispatch({
                            type: "selectedItemFieldChanged",
                            field: "contractId",
                            value: option?.value || null,
                          });
                          dispatch({ type: "selectedItemFieldChanged", field: "contractOption", value: option });
                          if (option?.trip) {
                            dispatch({ type: "selectedItemFieldChanged", field: "tripId", value: option.trip._id });
                            dispatch({
                              type: "selectedItemFieldChanged",
                              field: "tripOption",
                              value: formatTripOption(option.trip),
                            });
                          }
                          if (option?.clientId) {
                            dispatch({ type: "selectedItemFieldChanged", field: "clientId", value: option.clientId });
                            dispatch({
                              type: "selectedItemFieldChanged",
                              field: "clientOption",
                              value: { value: option.clientId, label: option.clientName || "" },
                            });
                          }
                        }}
                        value={state.selectedItem?.contractOption || null}
                        noOptionsMessage={() => "Type to search..."}
                        isClearable
                        menuPosition="fixed"
                      />
                    </div>
                  </div>
                )}

                {!state.selectedItem?._id && (
                  <div className="w-100 d-flex gap-20">
                    <div className="form-group">
                      <Checkbox
                        className="pl-0"
                        checked={state.selectedItem?.skipPaymentRecordAutoCreation}
                        onChange={(event) => {
                          dispatch({
                            type: "selectedItemFieldChanged",
                            field: "skipPaymentRecordAutoCreation",
                            value: event.target.checked,
                          });
                        }}
                        name="skipPaymentRecordAutoCreation"
                        id="skipPaymentRecordAutoCreation"
                      />
                      <label for="skipPaymentRecordAutoCreation">
                        {selectedItemTargetsIncidentals
                          ? "Don't create incidental payment record for the trip"
                          : "Don't create client payment record for the trip"}
                      </label>
                    </div>
                  </div>
                )}
              </>
            )}
          </form>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <LoadingButton
            className="base-button evo-blue flex-half"
            type="button"
            onClick={handleSubmit}
            isLoading={state.isSaving}
          >
            Submit
          </LoadingButton>
          <button
            className="base-button btn-danger flex-half"
            type="button"
            onClick={handleCloseModal}
            disabled={state.isSaving}
          >
            Cancel
          </button>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.isDeleteModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: { zIndex: 999999, backgroundColor: "rgb(32 53 68 / 50%)" },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>
            {state.selectedItem?.clientPaymentId || state.selectedItem?.incidentalChargeId
              ? "Unlink Payment from the Ledger?"
              : "Delete Ledger Record?"}
          </h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            {state.selectedItem?.incidentalChargeId
              ? "You are about to unlink the payment from the Funds Ledger. The related Funds Ledger record and Incidental Charge will also be permanently deleted. This action cannot be undone. Are you sure you want to continue?"
              : state.selectedItem?.clientPaymentId
                ? "You are about to unlink the payment from the Funds Ledger. The related Funds Ledger record and Client Payment will also be permanently deleted. This action cannot be undone. Are you sure you want to continue?"
                : "Are you sure you want to delete this ledger record? This action is permanent and cannot be undone."}
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseModal}
              disabled={state.isSaving}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              onClick={handleDeleteConfirm}
              isLoading={state.isSaving}
            >
              Delete Record
            </LoadingButton>
          </div>
        </div>
      </ReactModal>

      <PaymentUnlinkLedgerModal
        isOpen={state.isUnlinkModalOpen}
        row={state.itemToUnlink || {}}
        onRequestClose={handleCloseUnlinkModal}
      />

      {state.revertModal.isIncidental ? (
        <RevertIncidentalFundsModal
          open={state.revertModal.open}
          sourceInfo={state.revertModal.sourceInfo}
          targetInfo={state.revertModal.targetInfo}
          onSubmit={handleRevertConfirm}
          onClose={handleCloseModal}
          saving={state.isSaving}
        />
      ) : (
        <RevertFundsModal
          open={state.revertModal.open}
          sourceInfo={state.revertModal.sourceInfo}
          targetInfo={state.revertModal.targetInfo}
          onSubmit={handleRevertConfirm}
          onClose={handleCloseModal}
          saving={state.isSaving}
        />
      )}
    </Fragment>
  );
};

export default FundsLedgerContent;
